/**
 * Words and sums for a month in progress and for month-on-month change (SF-5117).
 *
 * While the month is in progress the API counts full days 1..N and compares them with the same
 * days of last month, so the screens say so. Percent change comes from the shared percentChange
 * (none when previous is 0 or missing); this file only sorts it into up, down or flat.
 */
import { percentChange } from '../../utils/format';
import type { DailyAverage, UsageProgress, UsageTotals } from './usageClient';

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** True only when the API says the month is in progress; false for an older API. */
export function isInProgress(progress: UsageProgress | null | undefined): boolean {
  return progress?.in_progress === true;
}

/** "YYYY-MM-DD" as "D MMM" (e.g. "14 Nov"); null when it does not parse. */
export function formatDayMonth(date: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
  if (!m) return null;
  const month = MONTHS_SHORT[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : null;
}

/** The banner for a month in progress; null for a past month or an older API. */
export function progressBannerText(progress: UsageProgress | null | undefined): string | null {
  if (!progress || !isInProgress(progress)) return null;
  if (progress.days_counted <= 0) return 'Month just started: no full day counted yet.';
  const through = formatDayMonth(progress.through);
  const upTo = through ? `figures up to ${through}` : 'figures up to yesterday';
  return `Month in progress: day ${progress.days_counted} of ${progress.days_in_month}, ${upTo}.`;
}

/** What the screens say for a month in progress with nothing counted yet (SF-5124). */
export const EMPTY_MONTH_TEXT = 'No feedback counted yet this month';

/**
 * True for a month in progress with nothing counted yet: no full day counted, or every total
 * still 0 (SF-5124). The screens then say so instead of showing 0s and -100%.
 */
export function isEmptyMonth(
  progress: UsageProgress | null | undefined,
  totals: UsageTotals | null | undefined,
): boolean {
  if (!isInProgress(progress)) return false;
  if (progress?.days_counted === 0) return true;
  if (!totals) return false;
  return (
    (totals.total_feedback ?? 0) === 0 &&
    (totals.ratings ?? 0) === 0 &&
    (totals.surveys?.total ?? 0) === 0
  );
}

/** What "last month" means beside this month's figures. */
export function comparisonCaption(progress: UsageProgress | null | undefined): string {
  return isInProgress(progress) ? 'vs same days last month' : 'vs last month';
}

/** Up, down, flat, or no comparison (no previous figure, or previous was 0). */
export type TrendKind = 'up' | 'down' | 'flat' | 'none';

export interface Trend {
  kind: TrendKind;
  /** Percent change; null when kind is 'none'. */
  pct: number | null;
}

/** The change from previous to current, as the screens show it. */
export function trendOf(
  current: number | null | undefined,
  previous: number | null | undefined,
): Trend {
  const pct = percentChange(current, previous);
  if (pct === null) return { kind: 'none', pct: null };
  if (pct === 0) return { kind: 'flat', pct: 0 };
  return { kind: pct > 0 ? 'up' : 'down', pct };
}

/**
 * A percent change as "+12%" or "−12%" (a true minus sign). Whole percents, except under 1% where
 * one decimal keeps a small move from reading as "+0%".
 */
export function formatChange(pct: number): string {
  const abs = Math.abs(pct);
  const digits = abs < 1 ? 1 : 0;
  const text = abs.toFixed(digits);
  return `${pct > 0 ? '+' : '\u2212'}${text}%`;
}

/** A per-day figure: "≈ 12.5 per day". Null when there is none (no full day counted). */
export function perDayText(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `\u2248 ${formatPerDay(value)} per day`;
}

/** A per-day figure to 1 decimal, as the API rounds it. */
export function formatPerDay(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value);
}

/** The per-day figure for one field, or null when the API sent none. */
export function dailyValue(
  avg: DailyAverage | null | undefined,
  field: keyof DailyAverage,
): number | null {
  const v = avg?.[field];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
